<template>
  <div class="anonymous-test-page">
    <!-- Header -->
    <div class="test-header">
      <div class="logo">
        <h1 class="p-m-0 p-text-primary p-text-2xl">TestHR</h1>
        <div class="p-flex p-flex-col p-ai-center p-ml-2">
          <span class="version-number p-font-semibold p-text-primary p-bg-white-transparent p-px-1 p-py-0.5 p-rounded">v{{ version }}</span>
          <span class="version-date p-text-secondary p-mt-0.5">{{ formatBuildDate(buildDate) }}</span>
        </div>
      </div>
      <div class="session-info">
        <h2 class="p-m-0 p-mb-2 p-text-text">{{ session?.name || 'Завантаження...' }}</h2>
        <p v-if="session?.description" class="p-m-0 p-text-secondary p-text-sm">{{ session.description }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="p-flex p-flex-col p-ai-center p-justify-center p-min-h-60vh p-text-center">
      <ProgressSpinner />
      <p>Завантаження тесту...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-flex p-flex-col p-ai-center p-justify-center p-min-h-60vh p-text-center">
      <i class="pi pi-exclamation-triangle p-text-6xl p-text-red-500 p-mb-4"></i>
      <h3>Помилка</h3>
      <p>{{ error }}</p>
      <Button 
        label="Спробувати знову" 
        icon="pi pi-refresh" 
        @click="loadSession"
        class="p-button-outlined"
      />
    </div>

    <!-- Test Content -->
    <div v-else-if="session && !testStarted" class="test-intro">
      <div class="intro-content">
        <i class="pi pi-file-edit p-text-6xl p-text-primary p-mb-6"></i>
        <h3 class="p-text-text p-mb-4">Ласкаво просимо до анонімного тестування</h3>
        <p class="p-text-secondary p-mb-8 p-leading-relaxed">Цей тест призначений для анонімного проходження. Ваші відповіді будуть збережені без прив'язки до особистості.</p>
        
        <div class="test-details">
          <div class="detail-item">
            <i class="pi pi-calendar p-text-primary"></i>
            <span>Тип тесту: {{ session.test_type?.name }}</span>
          </div>
          <div class="detail-item">
            <i class="pi pi-question-circle p-text-primary"></i>
            <span>Кількість питань: {{ session.test_type?.category === 'enps' ? 'Динамічна (залежить від оцінки)' : (session.questions?.length || 0) }}</span>
          </div>
          <div class="detail-item">
            <i class="pi pi-clock p-text-primary"></i>
            <span>Приблизний час: {{ estimatedTime }} хв</span>
          </div>
        </div>

        <div class="start-test-section">
          <Button 
            label="Почати тест" 
            icon="pi pi-play" 
            @click="startTest"
            class="p-button-success"
            :loading="startingTest"
          />
          <p class="p-text-secondary p-text-sm p-mt-4">Натиснувши кнопку, ви підтверджуєте, що проходите тест анонімно</p>
        </div>
      </div>
    </div>

    <!-- Test Form -->
    <div v-else-if="session && testStarted && !testCompleted" class="test-form">
      <div class="p-mb-6">
        <ProgressBar 
          :value="progressPercentage" 
          :showValue="false"
          class="progress-bar"
        />
        <div class="p-text-center p-mt-2 p-text-secondary p-text-sm">
          Питання {{ currentQuestionIndex + 1 }} з {{ filteredQuestions?.length }}
        </div>
      </div>

      <div class="question-container">
        <div class="question-header">
          <h3 class="p-text-xl p-font-semibold p-text-text">Питання {{ currentQuestionIndex + 1 }}</h3>
          <div class="question-type-badge">
            <Tag :value="getQuestionTypeLabel(currentQuestion?.question_type)" severity="secondary" />
          </div>
        </div>

        <div class="p-text-xl p-font-semibold p-text-text p-mb-6 p-leading-relaxed p-text-center">
          {{ currentQuestion?.text }}
        </div>

        <div v-if="currentQuestion?.help_text" class="p-mb-6 p-p-4 p-bg-surface-ground p-rounded-lg p-text-secondary p-text-sm p-text-center">
          <i class="pi pi-info-circle p-mr-2"></i>
          {{ currentQuestion.help_text }}
        </div>

        <!-- Question Input -->
        <div class="p-mb-6">
          <!-- Likert Scale -->
          <div v-if="isLikertScale" class="likert-scale">
            <div class="scale-labels">
              <span>Повністю не згоден</span>
              <span>Повністю згоден</span>
            </div>
            <div class="scale-options">
              <div 
                v-for="option in getLikertOptions()" 
                :key="option.value"
                class="scale-option"
                :class="{ selected: currentAnswer === option.value }"
                @click="setAnswer(option.value)"
              >
                <div class="option-value">{{ option.value }}</div>
                <div class="option-label">{{ option.label }}</div>
              </div>
            </div>
          </div>

          <!-- eNPS Scale -->
          <div v-else-if="isEnpsScale" class="enps-scale">
            <div class="scale-labels">
              <span>Не рекомендував би</span>
              <span>Рекомендував би</span>
            </div>
            <div class="scale-options">
              <div 
                v-for="option in getEnpsOptions()" 
                :key="option.value"
                class="scale-option"
                :class="{ selected: currentAnswer === option.value }"
                @click="setAnswer(option.value)"
              >
                <div class="option-value">{{ option.value }}</div>
              </div>
            </div>
          </div>

          <!-- Text Input -->
          <div v-else-if="isTextInput" class="text-input">
            <Textarea
              v-model="currentAnswer"
              :placeholder="getTextPlaceholder()"
              :rows="4"
              class="text-area"
              :autoResize="true"
            />
          </div>

          <!-- Yes/No -->
          <div v-else-if="isYesNo" class="yes-no-options">
            <Button
              :label="'Так'"
              :class="{ 'p-button-success': currentAnswer === 'yes' }"
              @click="setAnswer('yes')"
              class="p-button-outlined"
            />
            <Button
              :label="'Ні'"
              :class="{ 'p-button-danger': currentAnswer === 'no' }"
              @click="setAnswer('no')"
              class="p-button-outlined"
            />
          </div>
        </div>

        <!-- Navigation -->
        <div class="question-navigation">
          <Button 
            v-if="currentQuestionIndex > 0"
            label="Назад" 
            icon="pi pi-chevron-left" 
            @click="previousQuestion"
            class="p-button-outlined"
          />
          
          <Button 
            v-if="currentQuestionIndex < filteredQuestions?.length - 1"
            label="Далі" 
            icon="pi pi-chevron-right" 
            @click="nextQuestion"
            :disabled="!canProceed"
            class="p-button-primary"
          />
          
          <Button 
            v-else
            label="Завершити тест" 
            icon="pi pi-check-circle" 
            @click="completeTest"
            :disabled="!canProceed"
            class="p-button-success"
            :loading="completingTest"
          />
        </div>
      </div>
    </div>

    <!-- Test Completed -->
    <div v-else-if="testCompleted" class="test-completed">
      <div class="completion-content">
        <i class="pi pi-check-circle p-text-6xl p-text-green-500 p-mb-4"></i>
        <h3 class="p-text-text p-mb-4">Тест успішно завершено!</h3>
        <p class="p-text-secondary p-mb-8">Дякуємо за ваші відповіді. Вони збережені анонімно.</p>
        
        <div class="completion-details">
          <div class="detail-item">
            <i class="pi pi-clock p-text-primary"></i>
            <span>Час проходження: {{ formatDuration(testDuration) }}</span>
          </div>
          <div class="detail-item">
            <i class="pi pi-check p-text-primary"></i>
            <span>Відповідей: {{ filteredQuestions?.length }}</span>
          </div>
        </div>

        <div class="completion-actions">
          <Button 
            label="Закрити" 
            icon="pi pi-times" 
            @click="closeTest"
            class="p-button-outlined"
          />
        </div>
      </div>
    </div>
    
    <!-- Footer -->
    
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useStore } from 'vuex'
import ProgressSpinner from 'primevue/progressspinner'
import ProgressBar from 'primevue/progressbar'
import Button from 'primevue/button'
import Textarea from 'primevue/textarea'
import Tag from 'primevue/tag'
import Footer from '../components/Footer.vue'

export default {
  name: 'AnonymousTest',
  components: {
    ProgressSpinner,
    ProgressBar,
    Button,
    Textarea,
    Tag,
    Footer
  },
  setup() {
    const route = useRoute()
    const toast = useToast()
    const store = useStore()

    // Reactive data
    const session = ref(null)
    const loading = ref(true)
    const error = ref(null)
    const testStarted = ref(false)
    const testCompleted = ref(false)
    const startingTest = ref(false)
    const completingTest = ref(false)
    const currentQuestionIndex = ref(0)
    const currentAnswer = ref('')
    const answers = ref({})
    const testStartTime = ref(null)
    const testEndTime = ref(null)

    // Computed properties
    const currentQuestion = computed(() => {
      const question = session.value?.questions?.[currentQuestionIndex.value]
      return question
    })

    const progressPercentage = computed(() => {
      if (!session.value?.questions) return 0
      return ((currentQuestionIndex.value + 1) / filteredQuestions.value.length) * 100
    })

    const estimatedTime = computed(() => {
      if (!session.value?.questions) return 0
      return Math.ceil(filteredQuestions.value.length * 1.5) // 1.5 хв на питання
    })

    const testDuration = computed(() => {
      if (!testStartTime.value || !testEndTime.value) return null
      return testEndTime.value - testStartTime.value
    })

    const canProceed = computed(() => {
      if (!currentQuestion.value) return false
      
      if (currentQuestion.value.is_required && !currentAnswer.value) {
        return false
      }
      
      return true
    })

    // Filter questions for eNPS tests
    const filteredQuestions = computed(() => {
      if (!session.value?.questions) return []
      
      if (session.value.test_type?.category === 'enps') {
        // Find the main eNPS question
        const mainQuestion = session.value.questions.find(q => q.question_type === 'enps_scale')
        if (!mainQuestion) {
          return session.value.questions
        }
        
        const mainAnswer = answers.value[mainQuestion.id]
        
        // If main question is not answered yet, show only main question
        if (mainAnswer === undefined || mainAnswer === null) {
          return [mainQuestion]
        }
        
        // Filter questions based on main answer (EXACT COPY from TestSession.vue)
        let filteredQuestions = [mainQuestion]
        
        if (mainAnswer >= 9) {
          // Promoter questions (9-10) - показуємо питання 19, 20, 21
          const promoterQuestions = session.value.questions.filter(q => 
            q.question_type === 'text' && (
              q.text.includes('подобається') || 
              q.text.includes('мотивує') || 
              q.text.includes('особливо добре')
            )
          )
          filteredQuestions = filteredQuestions.concat(promoterQuestions)
        } else if (mainAnswer >= 7) {
          // Passive questions (7-8) - показуємо питання 23, 26
          const passiveQuestions = session.value.questions.filter(q => 
            q.question_type === 'text' && (
              q.text.includes('кращим') || 
              q.text.includes('покращень') ||
              q.text.includes('вищу оцінку')
            )
          )
          filteredQuestions = filteredQuestions.concat(passiveQuestions)
        } else {
          // Detractor questions (0-6) - показуємо питання 22, 27, 28, 29
          const detractorQuestions = session.value.questions.filter(q => 
            q.question_type === 'text' && (
              q.text.includes('не дуже добре') ||
              q.text.includes('розчарування') ||
              q.text.includes('змінити') ||
              q.text.includes('причини')
            )
          )
          filteredQuestions = filteredQuestions.concat(detractorQuestions)
        }
        
        return filteredQuestions
      }
      
      return session.value.questions
    })

    // Question type helpers
    const isLikertScale = computed(() => {
      return currentQuestion.value?.question_type === 'likert_scale' || 
             currentQuestion.value?.question_type === 'likert_scale_7'
    })

    const isEnpsScale = computed(() => {
      return currentQuestion.value?.question_type === 'enps_scale'
    })

    const isTextInput = computed(() => {
      return currentQuestion.value?.question_type === 'text'
    })

    const isYesNo = computed(() => {
      return currentQuestion.value?.question_type === 'yes_no'
    })

    // Methods
    const loadSession = async () => {
      try {
        loading.value = true
        error.value = null
        
        const uuid = route.params.uuid
        
        const response = await fetch(`/api/anonymous-sessions/${uuid}/`)
        
        if (!response.ok) {
          throw new Error('Сесія не знайдена або неактивна')
        }
        
        const sessionData = await response.json()
        
        session.value = sessionData
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    const startTest = async () => {
      try {
        startingTest.value = true
        
        // Generate device fingerprint
        const deviceFingerprint = await generateDeviceFingerprint()
        
        const response = await fetch(`/api/anonymous-sessions/${route.params.uuid}/start_anonymous_test/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            device_fingerprint: deviceFingerprint
          })
        })
        
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Помилка запуску тесту')
        }
        
        const result = await response.json()
        testStarted.value = true
        testStartTime.value = Date.now()
        
        // Store anonymous ID in localStorage
        localStorage.setItem('anonymous_test_id', result.anonymous_id)
        localStorage.setItem('anonymous_test_start', testStartTime.value.toString())
        
        toast.add({
          severity: 'success',
          summary: 'Тест розпочато',
          detail: result.detail,
          life: 3000
        })
        
        // Load first question
        await nextTick()
        loadCurrentQuestion()
        
      } catch (err) {
        toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: err.message,
          life: 5000
        })
      } finally {
        startingTest.value = false
      }
    }

    const generateDeviceFingerprint = async () => {
      // Simple device fingerprint based on user agent and screen resolution
      const userAgent = navigator.userAgent
      const screenRes = `${screen.width}x${screen.height}`
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      
      const fingerprint = `${userAgent}|${screenRes}|${timezone}`
      
      // Simple hash function
      let hash = 0
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32-bit integer
      }
      
      return Math.abs(hash).toString(36)
    }

    const loadCurrentQuestion = () => {
      if (!currentQuestion.value) return
      
      // Load previous answer if exists
      const questionId = currentQuestion.value.id.toString()
      currentAnswer.value = answers.value[questionId] || ''
    }

    const nextQuestion = () => {
      if (currentQuestionIndex.value < filteredQuestions.value.length - 1) {
        currentQuestionIndex.value++
        loadCurrentQuestion()
      }
    }

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--
        loadCurrentQuestion()
      }
    }

    const setAnswer = (answer) => {
      currentAnswer.value = answer
      
      // Save answer
      if (currentQuestion.value) {
        const questionId = currentQuestion.value.id.toString()
        answers.value[questionId] = answer
        
        // For eNPS tests, handle main question answer (EXACT COPY from TestSession.vue)
        if (session.value?.test_type?.category === 'enps' && 
            currentQuestion.value.question_type === 'enps_scale') {
          
          // Reset question index to show follow-up questions
          currentQuestionIndex.value = 0
          
          // Force re-computation of filteredQuestions
          nextTick(() => {
            console.log('=== ПІСЛЯ СКИДАННЯ ІНДЕКСУ ===')
            console.log('Filtered questions count:', filteredQuestions.value.length)
            console.log('Current question index:', currentQuestionIndex.value)
            console.log('Current question:', currentQuestion.value?.text?.substring(0, 50) + '...')
          })
        }
      }
    }

    const completeTest = async () => {
      try {
        completingTest.value = true
        
        // Save current answer
        if (currentQuestion.value) {
          const questionId = currentQuestion.value.id.toString()
          answers.value[questionId] = currentAnswer.value
        }
        
        // Submit test
        const anonymousId = localStorage.getItem('anonymous_test_id')
        if (!anonymousId) {
          throw new Error('Не знайдено ID тесту')
        }
        
        let response // Declare response variable outside conditional blocks
        
        // For eNPS tests, use the same logic as authorized users
        if (session.value?.test_type?.category === 'enps') {
          console.log('=== ВІДПРАВКА eNPS ТЕСТУ (АНОНІМНА СЕСІЯ) ===')
          
          // Find main eNPS question
          const mainQuestion = session.value.questions.find(q => q.question_type === 'enps_scale')
          if (!mainQuestion) {
            throw new Error('Основне питання eNPS не знайдено')
          }
          
          const mainScore = answers.value[mainQuestion.id]
          if (mainScore === undefined || mainScore === null) {
            throw new Error('Основне питання не відповідається')
          }
          
          console.log('Main score:', mainScore)
          
          // Create test data object with category-specific questions (EXACT COPY from TestSession.vue)
          const testData = {
            anonymous_id: anonymousId,
            responses: {}
          }
          
          // Add main question response
          testData.responses[mainQuestion.id] = mainScore
          
          // Add category-specific questions based on score
          if (mainScore >= 9) {
            // Promoter questions (9-10) - показуємо питання 19, 20, 21
            const promoterQuestions = session.value.questions.filter(q => 
              q.question_type === 'text' && (
                q.text.includes('подобається') || 
                q.text.includes('мотивує') || 
                q.text.includes('особливо добре')
              )
            )
            console.log('Found promoter questions:', promoterQuestions.length)
            promoterQuestions.forEach((q) => {
              const answer = answers.value[q.id] || ''
              testData.responses[q.id] = answer
              console.log(`Promoter question ${q.id}:`, answer)
            })
          } else if (mainScore >= 7) {
            // Passive questions (7-8) - показуємо питання 23, 26
            const passiveQuestions = session.value.questions.filter(q => 
              q.question_type === 'text' && (
                q.text.includes('кращим') || 
                q.text.includes('покращень') ||
                q.text.includes('вищу оцінку')
              )
            )
            console.log('Found passive questions:', passiveQuestions.length)
            passiveQuestions.forEach((q) => {
              const answer = answers.value[q.id] || ''
              testData.responses[q.id] = answer
              console.log(`Passive question ${q.id}:`, answer)
            })
          } else {
            // Detractor questions (0-6) - показуємо питання 22, 27, 28, 29
            const detractorQuestions = session.value.questions.filter(q => 
              q.question_type === 'text' && (
                q.text.includes('не дуже добре') ||
                q.text.includes('розчарування') ||
                q.text.includes('змінити') ||
                q.text.includes('причини')
              )
            )
            console.log('Found detractor questions:', detractorQuestions.length)
            detractorQuestions.forEach((q) => {
              const answer = answers.value[q.id] || ''
              testData.responses[q.id] = answer
              console.log(`Detractor question ${q.id}:`, answer)
            })
          }
          
          // Add other questions for completeness
          session.value.questions.forEach(q => {
            if (!testData.responses[q.id]) {
              testData.responses[q.id] = answers.value[q.id] || ''
            }
          })
          
          console.log('Final test data:', testData)
          
          response = await fetch('/api/anonymous-responses/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(testData)
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.detail || 'Помилка завершення eNPS тесту')
          }
          
        } else {
          // For regular tests, use existing logic
          response = await fetch('/api/anonymous-responses/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              anonymous_id: anonymousId,
              responses: answers.value
            })
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.detail || 'Помилка завершення тесту')
          }
        }
        
        const result = await response.json()
        testCompleted.value = true
        testEndTime.value = Date.now()
        
        // Clean up localStorage
        localStorage.removeItem('anonymous_test_id')
        localStorage.removeItem('anonymous_test_start')
        
        toast.add({
          severity: 'success',
          summary: 'Тест завершено',
          detail: result.detail,
          life: 5000
        })
        
      } catch (err) {
        toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: err.message,
          life: 5000
        })
      } finally {
        completingTest.value = false
      }
    }

    const closeTest = () => {
      // Close window or redirect
      if (window.opener) {
        window.close()
      } else {
        window.location.href = '/'
      }
    }

    const onThemeChanged = (newTheme) => {
      // Handle theme change from Footer
      // The theme is already managed by Vuex store, so we just need to apply it
      document.documentElement.setAttribute('data-theme', newTheme)
      document.body.className = document.body.className.replace(/theme-\w+/g, '')
      document.body.classList.add(`theme-${newTheme}`)
    }

    const getQuestionTypeLabel = (type) => {
      const labels = {
        'likert_scale': 'Шкала Лікерта (1-5)',
        'likert_scale_7': 'Шкала Лікерта (1-7)',
        'enps_scale': 'Шкала eNPS (0-10)',
        'yes_no': 'Так/Ні',
        'text': 'Текстова відповідь',
        'numeric': 'Числова відповідь'
      }
      return labels[type] || type
    }

    const getLikertOptions = () => {
      const max = currentQuestion.value?.question_type === 'likert_scale_7' ? 7 : 5
      const options = []
      
      for (let i = 1; i <= max; i++) {
        let label = ''
        if (max === 5) {
          switch (i) {
            case 1: label = 'Повністю не згоден'; break
            case 2: label = 'Не згоден'; break
            case 3: label = 'Нейтрально'; break
            case 4: label = 'Згоден'; break
            case 5: label = 'Повністю згоден'; break
          }
        } else {
          label = i.toString()
        }
        
        options.push({ value: i, label })
      }
      
      return options
    }

    const getEnpsOptions = () => {
      const options = []
      for (let i = 0; i <= 10; i++) {
        options.push({ value: i, label: i.toString() })
      }
      return options
    }

    const getTextPlaceholder = () => {
      return 'Введіть вашу відповідь...'
    }

    const formatDuration = (duration) => {
      if (!duration) return '0 хв'
      
      const minutes = Math.floor(duration / 60000)
      const seconds = Math.floor((duration % 60000) / 1000)
      
      if (minutes === 0) {
        return `${seconds} сек`
      }
      
      return `${minutes} хв ${seconds} сек`
    }

    const formatBuildDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('uk-UA');
    }

    // Lifecycle
    onMounted(async () => {
      // Load version info first
      await store.dispatch('fetchVersionInfo')
      // Then load session
      loadSession()
    })

    return {
      // Data
      session,
      loading,
      error,
      testStarted,
      testCompleted,
      startingTest,
      completingTest,
      currentQuestionIndex,
      currentAnswer,
      answers,
      testStartTime,
      testEndTime,
      
      // Computed
      currentQuestion,
      progressPercentage,
      estimatedTime,
      testDuration,
      canProceed,
      isLikertScale,
      isEnpsScale,
      isTextInput,
      isYesNo,
      filteredQuestions,
      
      // Methods
      loadSession,
      startTest,
      setAnswer,
      nextQuestion,
      previousQuestion,
      completeTest,
      closeTest,
      onThemeChanged,
      getQuestionTypeLabel,
      getLikertOptions,
      getEnpsOptions,
      getTextPlaceholder,
      formatDuration,
      formatBuildDate,
      
      // Static data
      version: computed(() => store.state.version || 'v0.2.2-alpha'),
      buildDate: computed(() => store.state.buildDate)
    }
  }
}
</script>

<style scoped>
/* ===== CUSTOM STYLES THAT CANNOT BE REPLACED WITH PRIMEVUE UTILITIES ===== */

/* ===== VERSION STYLES ===== */
.version-number {
  font-size: 0.7rem;
  line-height: 1;
  font-weight: 600;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
}

.version-date {
  font-size: 0.6rem;
  line-height: 1;
}

/* ===== HEADER STYLES ===== */
.test-header {
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ===== INTRO SECTION STYLES ===== */
.test-intro {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 2rem;
}

.intro-content {
  backdrop-filter: blur(10px);
  padding: 3rem;
  border-radius: 20px;
  text-align: center;
  max-width: 600px;
}

.test-details {
  margin-bottom: 2rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.start-test-section {
  text-align: center;
}

/* ===== QUESTION CONTAINER STYLES ===== */
.question-container {
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 20px;
  margin: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid;
}

.question-type-badge {
  margin-bottom: 1rem;
}

/* ===== SCALE OPTIONS STYLES ===== */
.likert-scale,
.enps-scale {
  text-align: center;
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.scale-options {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.scale-option {
  cursor: pointer;
  padding: 1rem;
  border: 2px solid;
  border-radius: 12px;
  min-width: 60px;
  transition: all 0.3s ease;
}

.scale-option:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.scale-option.selected {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: white;
}

.option-value {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.3rem;
}

.option-label {
  font-size: 0.8rem;
  line-height: 1.2;
}

/* ===== TEXT INPUT STYLES ===== */
.text-input {
  margin-bottom: 2rem;
}

.text-area {
  width: 100%;
  border: 2px solid;
  border-radius: 8px;
  padding: 1rem;
  font-size: 1rem;
  resize: vertical;
}

.text-area:focus {
  border-color: var(--primary-color);
  outline: none;
}

/* ===== YES/NO OPTIONS STYLES ===== */
.yes-no-options {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

/* ===== NAVIGATION STYLES ===== */
.question-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid;
}

/* ===== COMPLETION STYLES ===== */
.test-completed {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 2rem;
}

.completion-content {
  backdrop-filter: blur(10px);
  padding: 3rem;
  border-radius: 20px;
  text-align: center;
  max-width: 500px;
}

.completion-details {
  margin: 2rem 0;
  text-align: left;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.completion-actions {
  margin-top: 2rem;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 768px) {
  .test-header {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .intro-content,
  .completion-content {
    padding: 2rem;
    margin: 1rem;
  }
  
  .question-container {
    padding: 1rem;
  }
  
  .scale-options {
    gap: 0.5rem;
  }
  
  .scale-option {
    min-width: 50px;
    padding: 0.8rem;
  }
  
  .question-navigation {
    flex-direction: column;
    gap: 1rem;
  }
}

/* ===== FOOTER INTEGRATION ===== */
.anonymous-test-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Footer styling for AnonymousTest page */
:deep(.app-footer) {
  background: transparent;
  border-top: none;
  color: white;
  margin-top: auto;
  position: relative;
  z-index: 10;
}

:deep(.app-footer .footer-title) {
  color: rgba(255, 255, 255, 0.9);
}

:deep(.app-footer .footer-description) {
  color: rgba(255, 255, 255, 0.7);
}

:deep(.app-footer .footer-section-title) {
  color: rgba(255, 255, 255, 0.8);
}

:deep(.app-footer .footer-link) {
  color: rgba(255, 255, 255, 0.7);
}

:deep(.app-footer .footer-link:hover) {
  color: rgba(255, 255, 255, 0.9);
}

:deep(.app-footer .footer-copyright) {
  color: rgba(255, 255, 255, 0.6);
}

:deep(.app-footer .version-number) {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

:deep(.app-footer .build-date) {
  color: rgba(255, 255, 255, 0.6);
}


</style>
